import {
  faCalendarDays,
  faCheckCircle,
  faCircleExclamation,
  faClock,
  faCommentLines,
  faDownload,
  faLayerGroup
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Grid, Typography } from "@mui/material"
import { DangerousHtml } from "components/DangerousHtml"
import theme from "css/theme/light"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import { CertificateDownloadLink } from "shared/learning/components/CertificateRender"
import {
  StyledCardContent,
  StyledCardMedia,
  StyledDetailsDiv,
  StyledFlexBox,
  StyledFlexGrid,
  StyledFontAwesomeIcon,
  StyledRootCard
} from "./ExaminationInfoCard.styled"
import { ChipTag } from "components/ChipTag/ChipTag"

export const ExaminationInfoCard = (props) => {
  const { item, assignedCourse } = props
  const { examinationSummary, examinationCertificate } = item
  const { t } = useTranslation()

  const styleProps = {
    image: "/assets/learning/card_".concat(item.examinationType).concat(".jpg")
  }

  const completedRaw = item.examinationCertificate?.completedDate
  const startRaw = item.recertificationStartDate ?? item.recertification?.startDate
  const endRaw = item.recertificationEndDate ?? item.recertification?.endDate
  const isRecertActive = !!item.recertificationIsActive

  const completed = moment(completedRaw)
  const start = moment(startRaw)
  const end = moment(endRaw)
  const today = moment().startOf("day")
  const startDay = moment(startRaw).startOf("day")
  const endDay = moment(endRaw).endOf("day")

  const passedWithinRecertWindow =
    isRecertActive &&
    completedRaw &&
    startRaw &&
    endRaw &&
    completed.isValid() &&
    start.isValid() &&
    end.isValid() &&
    completed.isBetween(start, end, "day", "[]")

  const isRecertInProgress =
    isRecertActive &&
    startRaw &&
    endRaw &&
    startDay.isValid() &&
    endDay.isValid() &&
    today.isBetween(startDay, endDay, "day", "[]")

  const isRecertScheduled =
    isRecertActive && startRaw && endRaw && start.isValid() && end.isValid() && today.isBefore(start, "day")

  const passedWithoutRecert = !isRecertActive && examinationSummary.hasPassed

  const showPassed = passedWithinRecertWindow || passedWithoutRecert

  return (
    <>
      <StyledRootCard>
        <StyledCardMedia image={styleProps.image} title={item.examinationName} />
        <StyledDetailsDiv>
          <StyledCardContent>
            <StyledFlexGrid item gap="20px">
              <Grid xs={12} item justifyContent="space-between" flexDirection="column" flexWrap="wrap">
                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Typography variant="bodyXlarge" fontWeight="600" mb={0}>
                    {assignedCourse ? assignedCourse?.examinationName : item.examinationName}
                  </Typography>
                  <Box display="flex" gap={1}>
                    {showPassed ? (
                      <ChipTag
                        text={t("learning.titles.passed", "Passed")}
                        textColor={theme.palette.success.dark}
                        bgColor={theme.palette.success.light}
                        icon={faCheckCircle}
                      />
                    ) : isRecertInProgress ? (
                      <ChipTag
                        text={t("learning.titles.recertification_period", "Recertification period")}
                        textColor={theme.palette.sky[700]}
                        bgColor={theme.palette.sky[100]}
                        icon={faClock}
                      />
                    ) : isRecertScheduled ? (
                      <ChipTag
                        text={`${t("learning.titles.recertification_period_starts", "Recertification period starts")} ${start.format("YYYY-MM-DD")}`}
                        textColor={theme.palette.sky[700]}
                        bgColor={theme.palette.sky[100]}
                        icon={faCalendarDays}
                      />
                    ) : null}

                    {examinationSummary.isExpiresIn30Days && (
                      <ChipTag
                        text={t("learning.titles.expires_soon", "Expires soon")}
                        textColor={theme.palette.warning.dark}
                        bgColor={theme.palette.warning.light}
                        icon={faClock}
                      />
                    )}

                    {examinationSummary.hasExpired && (
                      <ChipTag
                        text={t("learning.titles.expired", "Expired")}
                        textColor={theme.palette.error.dark}
                        bgColor={theme.palette.error.light}
                        icon={faCircleExclamation}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              <Box>
                <Grid
                  pb={isRecertInProgress && !passedWithinRecertWindow && 2}
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}>
                  <Grid display="flex" alignItems="center" item>
                    <StyledFontAwesomeIcon icon={faLayerGroup} />
                    <Typography variant="bodySmall">
                      {`${item.examinationAreaCount} ${t("learning.titles.segments", "segments")}`}
                    </Typography>
                  </Grid>

                  <Grid display="flex" alignItems="center" item>
                    <StyledFontAwesomeIcon icon={faCommentLines} />
                    <Typography variant="bodySmall">
                      {item.examinationQuestionCount} {t("learning.titles.questions", "questions")}
                    </Typography>
                  </Grid>
                </Grid>
                {isRecertInProgress && !passedWithinRecertWindow && (
                  <Box display="flex" flexDirection="column">
                    <Typography variant="bodySmall" fontWeight={600} mb={0.5}>
                      {t("learning.titles.recert_in_progress_long", "Recertification period in progress")}
                    </Typography>

                    <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
                      {t("globa.ongoing_between", "Ongoing between")}{" "}
                      <Box component="span">
                        {moment(item?.recertificationStartDate).format("DD-MM-YYYY")}
                      </Box>{" "}
                      –{" "}
                      <Box component="span">{moment(item?.recertificationEndDate).format("DD-MM-YYYY")}</Box>.
                    </Typography>
                  </Box>
                )}
              </Box>
              {examinationSummary.isExpiresIn30Days && (
                <StyledFlexBox alignItems="center">
                  <Typography variant="bodySmall" fontWeight="600" color={theme.palette.error["main"]}>
                    {`${t("learning.titles.certificate_expires", "Certificate expires")} ${moment(
                      examinationSummary.date
                    ).format("DD-MM-YYYY")}`}
                  </Typography>
                </StyledFlexBox>
              )}
              {examinationSummary.hasExpired && examinationSummary.date != null && (
                <StyledFlexBox alignItems="center">
                  <Typography variant="bodySmall" fontWeight="600" color={theme.palette.error["main"]}>
                    {`${t("learning.titles.certificate_expired", "Certificate has expired")} ${moment(
                      examinationSummary.date
                    ).format("DD-MM-YYYY")}`}
                  </Typography>
                </StyledFlexBox>
              )}
              <Box>
                <Typography variant="bodySmall" color="textSecondary">
                  <DangerousHtml>
                    {assignedCourse
                      ? assignedCourse?.examinationDescription
                          .replace(/<[^>]+>/g, "")
                          .substring(0, 150)
                          .concat("...")
                      : item.examinationDescription
                          .replace(/<[^>]+>/g, "")
                          .substring(0, 150)
                          .concat("...")}
                  </DangerousHtml>
                </Typography>
              </Box>

              {examinationSummary.hasPassed && examinationCertificate.completedDate && (
                <Grid
                  container
                  backgroundColor="default.light"
                  mt={1}
                  py={2.5}
                  px={3}
                  alignItems="center"
                  justifyContent="space-between"
                  borderRadius={2}>
                  <StyledFlexBox item columnGap="8px" alignItems="center">
                    <Typography
                      fontSize={theme.typography.bodyMedium}
                      fontWeight={500}
                      color={theme.palette.secondary["600"]}>
                      {t("learning.result.titles.downloadCert", "Download certificate")}
                    </Typography>
                  </StyledFlexBox>

                  <Box m={0}>
                    <CertificateDownloadLink item={item}>
                      <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <FontAwesomeIcon color={theme.palette.secondary["600"]} size="1x" icon={faDownload} />
                      </Grid>
                    </CertificateDownloadLink>
                  </Box>
                </Grid>
              )}
            </StyledFlexGrid>
            <Box display="flex" justifyContent="flex-start" gap={2} pt={3}>
              <Button
                variant="outlined"
                color="primary"
                component={NavLink}
                to={{
                  pathname: allRoutes.LearningUserExamsDetails(item.userExaminationId)
                }}>
                {t("global.buttons.readmore", "Read more")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                component={NavLink}
                to={allRoutes.LearningUserExamsExamination(item.userExaminationId)}>
                {t("learning.admin.overview.buttons.startCourse", "Start course")}
              </Button>
            </Box>
          </StyledCardContent>
        </StyledDetailsDiv>
      </StyledRootCard>
    </>
  )
}
